import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Eye,
  Loader2,
  Mail,
  Phone,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import {
  deleteCompany,
  getCompanies,
  markCompanyAsPaid,
  sendOtpToSuperAdmin,
  verifyOtp,
} from "@/services/superAdminService";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const ITEMS_PER_PAGE = 9;

const CompaniesList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState();
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
const [markPaidOpen, setMarkPaidOpen] = useState(false);
const [subscriptionStartDate, setSubscriptionStartDate] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, [statusFilter, searchTerm]);

  const fetchCompanies = async () => {
    setCurrentPage(1);
    try {
      setLoading(true);
      const params = {
        search: searchTerm || undefined,
        status_filter: statusFilter !== "all" ? statusFilter : undefined,
      };

      const response = await getCompanies(params);
      setCompanies(response.companies || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyles = (status) => {
    if (status === "active") {
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700";
    }
    return "bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground border border-border";
  };

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return companies.slice(startIndex, endIndex);
  }, [companies, currentPage]);

  const totalPages = Math.ceil(companies.length / ITEMS_PER_PAGE);

  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "default" : "outline"}
          size="icon"
          onClick={() => handlePageChange(i)}
          className={
            i !== currentPage
              ? "bg-card hover:bg-muted"
              : "bg-primary hover:bg-primary"
          }
        >
          {i}
        </Button>
      );
    }

    return pages;
  };

  const handleVerifyOtpAndDelete = async () => {
    try {
      setVerifying(true);

      const res = await verifyOtp(otp, selectedCompany);
      toast.success(`${res.data.message}`);
      setVerifying(false);

      setOtp("");
      setOtpOpen(false);
      fetchCompanies();
      return true;
    } catch (error) {
      setOtp("");
      setVerifying(false);
      toast.error(error?.response.data.error || "Failed to verify OTP");
    }
  };

  const handleMarkAsPaid = async (companyId, subscriptionStartDate) => {
    try {
      await markCompanyAsPaid(companyId, subscriptionStartDate);
      toast.success("Company marked as paid successfully");
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to mark company as paid");
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            Companies
          </h1>
          <p className="mt-1 text-muted-foreground">
            Manage all organizations registered on the platform
          </p>
        </div>

        <Link to="/super-admin/companies/add">
          <Button className="mt-4 sm:mt-0">
            <Plus className="h-4 w-4 mr-2" /> Add Company
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="border border-border sticky top-0 z-10 shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search company or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Loader / Empty */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : companies.length === 0 ? (
        <Card className="border-dashed border-2 border-border">
          <CardContent className="p-16 text-center">
            <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No companies found
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters or search terms."
                : "Start by adding your first company."}
            </p>
            <Link to="/super-admin/companies/add">
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Add Company
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Companies Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedData.map((company) => (
              <Card
                key={company.company_id}
                onClick={() =>
                  navigate("/super-admin/companies/details", {
                    state: { companyId: company.id },
                  })
                }
                className="group relative border border-border hover:shadow-xl hover:-translate-y-1 hover:bg-muted/50 transition-all duration-200 rounded-xl overflow-hidden cursor-pointer"
              >
                <div className="bg-muted border-b border-border px-5 py-4">
                  {/* Icon + Name + Status */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center border border-primary shrink-0">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>

                    <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                      <h3 className="text-lg font-semibold leading-tight truncate text-foreground">
                        {company.name || "Unnamed Company"}
                      </h3>

                      {/* Status and the paid action share a row — the button has
                          no room beside the name, and this space was empty. */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`w-fit px-3 py-0.5 rounded-full text-xs font-medium shadow-sm ${getStatusStyles(
                          company.status
                        )}`}
                        >
                          {company.status === "active" ? "Active" : "Inactive"}
                        </span>

                        {company.subscription_info?.end_date && !company.subscriptions?.[0]?.razorpay_subscription_id &&
                          (() => {
                            const end = new Date(company.subscription_info.end_date).getTime();
                            const hoursLeft = (end - Date.now()) / (1000 * 60 * 60);
                            return hoursLeft <= 48;
                          })() && (
                            <div className="ml-auto">
                      <Dialog
                        open={markPaidOpen}
                        onOpenChange={(open) => {
                          setMarkPaidOpen(open);
                          if (!open) setSubscriptionStartDate("");
                        }}
                      >
                        <DialogTrigger asChild>
                          <button
                            className="group inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-700/30
                 bg-gradient-to-b from-emerald-500 to-emerald-600 px-4 py-2
                 text-xs font-semibold text-white shadow-sm shadow-emerald-900/20
                 transition-all duration-200
                 hover:-translate-y-0.5 hover:from-emerald-400 hover:to-emerald-500 hover:shadow-md hover:shadow-emerald-900/25
                 active:translate-y-0 active:shadow-sm
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(company.id);
                              setMarkPaidOpen(true);
                            }}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 transition-transform duration-200 group-hover:scale-110" />
                            Mark as Paid
                          </button>
                        </DialogTrigger>

                        <DialogContent className="dark:bg-slate-900 max-w-md">
                          <DialogHeader>
                            <DialogTitle>Mark Subscription as Paid</DialogTitle>
                            <DialogDescription>
                              Select the subscription start date for this company.
                            </DialogDescription>
                          </DialogHeader>

                          {/* DATE INPUT */}
                          <div className="space-y-2">
                            <Label htmlFor="subscription-start-date">
                              Subscription Start Date <span className="text-red-500">*</span>
                            </Label>
                            <input
                              id="subscription-start-date"
                              type="date"
                              required
                              value={subscriptionStartDate}
                              onChange={(e) => setSubscriptionStartDate(e.target.value)}
                              className="w-full rounded-md border border-border px-3 py-2 bg-background"
                            />
                          </div>

                          {/* ACTIONS */}
                          <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>
                              Cancel
                            </Button>

                            <Button
                              disabled={!subscriptionStartDate}
                              onClick={() => {
                                handleMarkAsPaid(selectedCompany, subscriptionStartDate);
                                setMarkPaidOpen(false);
                                setSubscriptionStartDate("");
                              }}>
                              Confirm & Mark Paid
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center text-sm text-foreground">
                    <Mail className="h-4 w-4 mr-2 text-primary" />
                    <span className="truncate">{company.contact_email}</span>
                  </div>

                  {company.phone && (
                    <div className="flex items-center text-sm text-foreground">
                      <Phone className="h-4 w-4 mr-2 text-primary" />
                      <span>{company.phone}</span>
                    </div>
                  )}

                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Created {formatDate(company.createdAt)}</span>
                  </div>

                  <div
                    className="pt-4 flex gap-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="flex-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-primary text-primary hover:bg-emerald-50 dark:hover:bg-primary/20"
                        onClick={() =>
                          navigate("/super-admin/companies/details", {
                            state: { companyId: company.id },
                          })
                        }
                      >
                        <Eye className="h-4 w-4 mr-2" /> View
                      </Button>
                    </span>
                    <span className="flex-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-border hover:bg-muted hover:text-black dark:hover:text-white"
                        onClick={() =>
                          navigate("/super-admin/companies/edit", {
                            state: { companyId: company.id },
                          })
                        }
                      >
                        <Edit className="h-4 w-4 mr-2 text-muted-foreground" />
                        Edit
                      </Button>
                    </span>
                    <span className="flex-1">
                      <Button
                        variant="outlined"
                        size="sm"
                        className="w-full border border-red-500 text-red-500 hover:bg-primary/10 dark:hover:bg-primary/20"
                        onClick={() => {
                          setSelectedCompany(company.id);
                          setConfirmAction("delete");
                          setConfirmModal(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </Button>
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center pt-6">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {renderPageNumbers()}

                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && companies.length > 0 && (
        <div className="text-center text-sm text-muted-foreground mt-4">
          Showing{" "}
          {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, companies.length)} -{" "}
          {Math.min(currentPage * ITEMS_PER_PAGE, companies.length)} of{" "}
          {companies.length} total{" "}
          {companies.length === 1 ? "company" : "companies"}
        </div>
      )}

      <Dialog
        open={otpOpen}
        onOpenChange={(open) => {
          if (verifying) return;
          setOtpOpen(open);
          if (!open) setOtp("");
        }}
      >
        <DialogContent
          className="dark:bg-slate-900"
          onInteractOutside={(e) => verifying && e.preventDefault()}
          onEscapeKeyDown={(e) => verifying && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Verify OTP</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit OTP sent to your email.
            </p>
          </DialogHeader>

          {/* OTP INPUTS */}
          <div className="flex justify-center gap-2 py-4">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <input
                key={idx}
                type="text"
                maxLength={1}
                value={otp[idx] ?? ""}
                className="h-10 w-10 text-center border rounded-md bg-background"
                disabled={verifying}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => {
                  const value = e.target.value;
                  if (!/^\d?$/.test(value)) return;
                  const next = otp.split("");
                  next[idx] = value;
                  const newOtp = next.join("");
                  setOtp(newOtp);

                  if (value && idx < 5) {
                    document.getElementById(`otp-${idx + 1}`)?.focus();
                  }
                }}
                id={`otp-${idx}`}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !otp[idx] && idx > 0) {
                    document.getElementById(`otp-${idx - 1}`)?.focus();
                  }
                }}
                onPaste={(e) => {
                  const paste = e.clipboardData
                    .getData("text")
                    .replace(/\D/g, "")
                    .slice(0, 6);
                  if (!paste) return;
                  const arr = new Array(6).fill("");
                  for (let i = 0; i < paste.length; i++) arr[i] = paste[i];
                  setOtp(arr.join(""));
                  const nextIndex = Math.min(paste.length - 1, 5);
                  document.getElementById(`otp-${nextIndex}`)?.focus();
                  e.preventDefault();
                }}
              />
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={verifying}
              onClick={() => {
                setOtp("");
                setOtpOpen(false);
              }}
            >
              Cancel
            </Button>

            <Button
              disabled={otp.length !== 6 || verifying}
              onClick={handleVerifyOtpAndDelete}
            >
              {verifying ? "Verifying..." : "Verify & Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmModal}
        onOpenChange={() => {
          setConfirmModal(false);
          setConfirmAction(null);
        }}
        title={
          confirmAction === "delete"
            ? "Are you sure you want to delete this company?"
            : "Mark this company as paid?"
        }
        destructive={confirmAction === "delete"}
        description={
          confirmAction === "markPaid"
            ? "This will extend the company subscription and update payment status."
            : undefined
        }
        onConfirm={async () => {
          try {
            setConfirmModal(false);

            if (confirmAction === "delete") {
              setOtp("");
              setOtpOpen(true);
              setOtpSending(true);

              const res = await sendOtpToSuperAdmin();
              if (res) toast.success("OTP sent to your email");
            }

            if (confirmAction === "markPaid") {
              await handleMarkAsPaid(selectedCompany);
            }
          } catch (error) {
            toast.error(
              error?.response?.data?.message || "Action failed. Try again."
            );
          } finally {
            setOtpSending(false);
            setConfirmAction(null);
          }
        }}
      />
    </div>
  );
};

export default CompaniesList;
